import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";
import { LayoutDashboard, ShoppingBag, Package, LogOut, PawPrint, TrendingUp, Truck, CheckCircle2, Star } from "lucide-react";
import AdminOrders from "./AdminOrders";
import AdminProducts from "./AdminProducts";
import AdminReviews from "./AdminReviews";
import { setMeta } from "../lib/seo";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="neo-card p-5 bg-white" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <span
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: color, border: "2px solid #0F172A", color: "#0F172A" }}
        >
          <Icon size={20} />
        </span>
        <div>
          <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{label}</div>
          <div className="font-display text-2xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function DashboardHome() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Dashboard</h1>
      <p className="text-sm text-slate-600">Overview of orders and inventory.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="admin-stats-grid">
        <StatCard icon={ShoppingBag} label="Total Orders" value={stats?.total_orders ?? "-"} color="#FDE047" />
        <StatCard icon={TrendingUp} label="New" value={stats?.new_orders ?? "-"} color="#38BDF8" />
        <StatCard icon={CheckCircle2} label="Delivered" value={stats?.delivered ?? "-"} color="#A3E635" />
        <StatCard icon={Package} label="Products" value={stats?.products_count ?? "-"} color="#F472B6" />
      </div>
      <div className="neo-card mt-6 p-6 bg-white">
        <div className="flex items-center gap-2 font-display text-lg font-semibold">
          <Truck size={18} /> Total Revenue (all-time)
        </div>
        <div className="font-display text-4xl font-bold mt-2">
          {stats ? `${stats.total_revenue.toFixed(0)} EGP` : "—"}
        </div>
        <p className="text-xs text-slate-500 mt-1">Includes shipping; revenue is recorded on order creation.</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();

  useEffect(() => {
    setMeta("robots", "noindex, nofollow");
    return () => setMeta("robots", "index, follow");
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex" style={{ background: "#F8FAFC" }} data-testid="admin-dashboard">
      <aside className="hidden md:flex flex-col w-64 bg-white border-r-2 border-slate-900 p-5">
        <div className="flex items-center gap-2 mb-8">
          <span
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "var(--pm-dog)", border: "2px solid #0F172A" }}
          >
            <PawPrint className="text-white" size={18} />
          </span>
          <span className="font-display text-lg font-bold">Pet Munchies</span>
        </div>
        <nav className="flex flex-col gap-2 text-sm font-semibold">
          <SideLink to="/admin" icon={LayoutDashboard} label="Dashboard" exact />
          <SideLink to="/admin/orders" icon={ShoppingBag} label="Orders" />
          <SideLink to="/admin/products" icon={Package} label="Products" />
          <SideLink to="/admin/reviews" icon={Star} label="Reviews" />
        </nav>
        <div className="mt-auto">
          <div className="text-xs text-slate-500 mb-2">Signed in as</div>
          <div className="font-semibold text-sm truncate">{user.email}</div>
          <button
            onClick={logout}
            className="neo-btn mt-3 w-full py-2 text-sm bg-white flex items-center justify-center gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-8 max-w-6xl">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: "var(--pm-dog)", border: "2px solid #0F172A" }}
            >
              <PawPrint className="text-white" size={14} />
            </span>
            <span className="font-display font-bold">Admin</span>
          </div>
          <button onClick={logout} className="neo-btn px-3 py-1.5 text-xs bg-white" data-testid="admin-logout-btn-mobile">Logout</button>
        </div>
        <div className="md:hidden flex gap-2 mb-4 overflow-x-auto">
          <SideLink to="/admin" icon={LayoutDashboard} label="Home" exact />
          <SideLink to="/admin/orders" icon={ShoppingBag} label="Orders" />
          <SideLink to="/admin/products" icon={Package} label="Products" />
          <SideLink to="/admin/reviews" icon={Star} label="Reviews" />
        </div>

        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="reviews" element={<AdminReviews />} />
        </Routes>
      </main>
    </div>
  );
}

function SideLink({ to, icon: Icon, label, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={`admin-nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          isActive ? "bg-yellow-300 border-2 border-slate-900" : "hover:bg-slate-100"
        }`
      }
    >
      <Icon size={16} /> {label}
    </NavLink>
  );
}
